import type { EntityCtx } from '@cli-types/next-adapter/generators.js'
import { toKebabCase } from '@cli-utils/text.js'
import { afterHook } from '@next-adapter/generators/actions/lifecycle-hook-emissions/after-hook.js'
import { beforeHook } from '@next-adapter/generators/actions/lifecycle-hook-emissions/before-hook.js'
import { lifecycleContext } from '@next-adapter/generators/actions/lifecycle-hook-emissions/lifecycle-context.js'
import { lifecycleHookImport } from '@next-adapter/generators/actions/lifecycle-hook-emissions/lifecycle-hook-import.js'
import { renderGenCreateContentServerActionTemplate } from '@next-adapter/templates/renderers/generators/actions/entity-file-contents/gen-create-content.js'

export function genCreateContent(ctx: EntityCtx): string {
  const kebabSingular = toKebabCase(ctx.singular)
  const slugSourceValue = ctx.slugSourceField ? `input.${ctx.slugSourceField}` : 'undefined'
  const snapshotImport = ctx.hasM2M
    ? `build${ctx.Singular}VersionSnapshotWithRelationships`
    : `build${ctx.Singular}VersionSnapshot`
  const snapshotData = ctx.hasM2M
    ? `await build${ctx.Singular}VersionSnapshotWithRelationships(createdRow as unknown as ${ctx.Singular}, tx)`
    : `build${ctx.Singular}VersionSnapshot(createdRow as unknown as ${ctx.Singular})`
  const markdownImport = ctx.hasHtmlOutput
    ? `\nimport { renderMarkdownSync } from './markdown'`
    : ''
  const createContext = lifecycleContext(
    ctx.plural,
    `,\n      input: input as unknown as Record<string, unknown>,\n      changedFields`
  )
  const createResultContext = lifecycleContext(
    ctx.plural,
    `,\n      entityId: created${ctx.Singular}Id,\n      input: input as unknown as Record<string, unknown>,\n      data: created${ctx.Singular}Data as unknown as Record<string, unknown>,\n      changedFields`
  )
  const createBeforePublishBlock = ctx.hasDraft
    ? `\n    const isCreatingPublished = input.published === true\n    if (isCreatingPublished) {\n      ${beforeHook('beforePublish', `{ ...lifecycleContext, changedFields: ['published'] }`)}\n    }\n`
    : ''
  const createAfterPublishBlock = ctx.hasDraft
    ? `\n    if (result[0]?.published === true) {\n      ${afterHook('afterPublish', `{ ...created${ctx.Singular}Context, changedFields: ['published'] }`)}\n    }`
    : ''

  return renderGenCreateContentServerActionTemplate({
    ctxTableVar: ctx.tableVar,
    ctxCacheInvalidationFn: ctx.cacheInvalidationFn,
    ctxCamelPlural: ctx.camelPlural,
    ctxSingular: ctx.Singular,
    kebabSingular: kebabSingular,
    snapshotImport: snapshotImport,
    lifecycleHookImport: lifecycleHookImport(),
    markdownImport: markdownImport,
    createContext: createContext,
    beforeHook: beforeHook('beforeCreate', 'lifecycleContext'),
    createBeforePublishBlock: createBeforePublishBlock,
    slugSourceValue: slugSourceValue,
    toKebabCase: toKebabCase(ctx.singular),
    ctxHtmlCreateBlock: ctx.htmlCreateBlock,
    ctxCreateMappings: ctx.createMappings,
    ctxHasHtmlOutputContent: ctx.hasHtmlOutput ? `\n${ctx.htmlCreateMappings},` : '',
    ctxHasDraftContent: ctx.hasDraft ? `\n      published: input.published ?? false,` : '',
    ctxPlural: ctx.plural,
    snapshotData: snapshotData,
    ctxSingularText: ctx.singular,
    createResultContext: createResultContext,
    afterHook: afterHook('afterCreate', `created${ctx.Singular}Context`),
    createAfterPublishBlock: createAfterPublishBlock,
    ctxCamelSingular: ctx.camelSingular
  })
}
